/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/// <reference path="types/MicrosoftMaps/Microsoft.Maps.All.d.ts" />

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
// import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import * as atlas from 'azure-maps-control'; // Import Azure Maps control
import { IDobbiesMapData } from './dobbies-map.data';
import { IDobbiesMapProps, IPushpinOptionsData } from './dobbies-map.props.autogenerated';
import { IFullOrgUnitAvailability } from '../../themes/dobbies/actions/utilities/full-org-unit-availability';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';

/**
 * Header Interface.
 */
export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
}

/**
 * MapViewProps Interface.
 */
export interface IMapViewProps extends IDobbiesMapProps<IDobbiesMapData> {
    ModuleProps: IModuleProps;
    Header: IHeader;
    MapProps: INodeProps;
    Map: atlas.Map | undefined; // Change from Microsoft.Maps.Map to atlas.Map
}

/**
 * Azure Maps component.
 * @extends {React.Component<IDobbiesMapProps<IDobbiesMapData>>}
 */
@observer
class Map extends React.PureComponent<IDobbiesMapProps<IDobbiesMapData>> {
    @observable public map: atlas.Map | undefined; // Change from Microsoft.Maps.Map to atlas.Map

    private readonly mapRef: React.RefObject<HTMLElement> = React.createRef<HTMLElement>();

    public constructor(props: IDobbiesMapProps<IDobbiesMapData>) {
        super(props);
    }

    public componentDidMount(): void {
        const {
            context: {
                // telemetry,
                actionContext: {
                    // requestContext: { channel }
                }
            }
        } = this.props;

        // Use the provided Primary Key for authentication
        const azureMapsAuthOptions: atlas.AuthenticationOptions = {
            authType: atlas.AuthenticationType.subscriptionKey,
            subscriptionKey: '89mOAQw5I72jNOmsvvQ19hwMiLBIbdiVMb73GTK5xxVNtcMUZB9CJQQJ99AGACi5Ypz75dUeAAAgAZMPWHfw'
        };
        this._initMap(azureMapsAuthOptions);

        reaction(
            () => {
                return [
                    this.props.data.storeSelectorStateManager?.result?.context?.orgUnitStoreInformation,
                    this.props.data.storeSelectorStateManager?.result?.selectedStoreLocationId,
                    this.map
                ];
            },
            () => {
                this._updateMap();
            }
        );
    }

    public render(): JSX.Element | null {
        const {
            config: { heading, className },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const shouldDisplayMap = storeSelectorStateManager?.listMapViewState.displayMap;

        const viewProps: IMapViewProps = {
            ...this.props,
            ModuleProps: {
                tag: 'div',
                moduleProps: this.props,
                className: classname('ms-map', { show: shouldDisplayMap }, className)
            },
            Header: {
                headerProps: { className: 'ms-map__header' },
                heading: heading?.text && (
                    <Msdyn365.Text
                        className='ms-map__heading'
                        {...heading}
                        tag={heading.tag || 'h2'}
                        text={heading.text}
                        editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            MapProps: {
                tag: 'div',
                className: 'ms-map__body h-100',
                ref: this.mapRef
            },
            Map: this.map
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Method to be called on heading change.
     * @param event -Content Edit Event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Initialize the map with Azure Maps.
     */
    private readonly _initMap = (authOptions: atlas.AuthenticationOptions) => {
        const {
            context: {
                actionContext: {
                    // requestContext: { channel }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;
        // debugger;

        this.map = new atlas.Map(this.mapRef.current as HTMLElement, {
            center: [0, 0], // Default center position
            zoom: 8, // Default zoom level
            authOptions: authOptions, // Use Azure Maps authentication options
            view: 'Auto'
        });

        this.map.controls.add(new atlas.control.ZoomControl(), {
            position: atlas.ControlPosition.TopRight
        });

        this.map.controls.add(new atlas.control.StyleControl(), {
            position: atlas.ControlPosition.TopRight
        });

        this.map.controls.add(new atlas.control.ScaleControl(), {
            position: atlas.ControlPosition.BottomRight
        });

        this.map.events.add('ready', () => {
            // this.map?.controls.add(new atlas.control.ZoomControl(), {
            //     position: 'top-right' as atlas.ControlPosition
            // });

            this.map?.events.add('click', () => {
                storeSelectorStateManager?.setSelectedStoreLocationId(undefined);
            });
            // Get current location and center the map
            this._getCurrentLocation();
        });
    };
    private readonly _getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                position => {
                    const { latitude, longitude } = position.coords;
                    // Center the map to the user's current location

                    this.map?.setCamera({
                        center: [longitude, latitude],
                        zoom: 12 // Zoom level to set, adjust as needed
                    });
                    debugger;
                },

                error => {
                    console.error('Error getting geolocation:', error);
                    // Handle error (e.g., display a message to the user)
                }
            );
        } else {
            console.error('Geolocation is not supported by this browser.');
            // Handle case where geolocation is not supported
        }
    };

    /**
     * Update the map with markers.
     */

    private readonly _updateMap = () => {
        const pushpinOptions = this.props.config.pushpinOptions;
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        const orgUnitStoreInformation: IFullOrgUnitAvailability[] | undefined = storeSelectorStateManager?.context?.orgUnitStoreInformation;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;

        if (this.map && orgUnitStoreInformation) {
            let mapLocation: OrgUnitLocation | undefined;
            this.map.markers.clear();

            const storeLocationList = orgUnitStoreInformation.filter(store => store.OrgUnitAvailability !== undefined);
            const markers: atlas.HtmlMarker[] = [];

            for (const [index, unitStoreInfo] of storeLocationList.entries()) {
                const storeLocation = unitStoreInfo.OrgUnitAvailability?.OrgUnitLocation;

                if (storeLocation?.Latitude && storeLocation.Longitude) {
                    const isSelectedLocation = selectedStoreLocationId === storeLocation.OrgUnitNumber;
                    const options = this._getPushpinOptions(isSelectedLocation, index, pushpinOptions);
                    const marker = new atlas.HtmlMarker({
                        position: [storeLocation.Longitude, storeLocation.Latitude],
                        htmlContent: options.icon
                    });

                    this.map.events.add('click', marker, () => {
                        this.handleClickEvent(storeLocation.OrgUnitNumber);
                    });
                    isSelectedLocation ? markers.unshift(marker) : markers.push(marker);

                    if (isSelectedLocation) {
                        mapLocation = storeLocation;
                    }
                }
            }

            // Add markers to the map
            this.map.markers.add(markers);

            // Add a slight delay to ensure markers are rendered
            setTimeout(() => {
                if (mapLocation) {
                    const currentLocation = [mapLocation.Longitude, mapLocation.Latitude] as atlas.data.Position;
                    this.map?.setCamera({ center: currentLocation, zoom: 10 }); // Adjust zoom level as needed
                } else if (markers.length > 0) {
                    // Extract postions, ensuring all are defined
                    const positions: atlas.data.Position[] = markers
                        .map(marker => marker.getOptions().position)
                        .filter((position): position is atlas.data.Position => position !== undefined);

                    const bounds = atlas.data.BoundingBox.fromPositions(positions);
                    this.map?.setCamera({ bounds });
                }
            }, 500); // Adjust the delay time as needed
        }
    };
    /**
     * Get the pushpin option.
     * @param isSelectedLocation - Is selected location.
     * @param index - Index.
     * @param pushpinOptions - PushpinOptions.
     * @returns - The pushpin options.
     */
    private readonly _getPushpinOptions = (isSelectedLocation: boolean, index: number, pushpinOptions?: IPushpinOptionsData) => {
        const text = pushpinOptions?.showIndex ? (index + 1).toString() : undefined;

        const size = pushpinOptions?.size || 1;
        const color = isSelectedLocation ? pushpinOptions?.selectionColor || pushpinOptions?.color : pushpinOptions?.color;
        const icon = this._getSvgIcon(size, color, text);

        return {
            color: isSelectedLocation ? pushpinOptions?.selectionColor : pushpinOptions?.color,
            icon
        };
    };
    /**
     * Generate SVG icon for pushpin.
     * @param size - Size of the icon.
     * @param color - Color of the icon.
     * @param text - Text inside the icon.
     * @returns - SVG icon as a string.
     */
    private readonly _getSvgIcon = (size: number, color?: string, text?: string) => {
        const baseValue: number = 32;
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${baseValue * size}" height="${baseValue * size}" viewBox="0 0 365 560">
                    <path fill="${color}" d="M182.9,551.7c0,0.1,0.2,0.3,0.2,0.3S358.3,283,358.3,194.6c0-130.1-88.8-186.7-175.4-186.9 C96.3,7.9,7.5,64.5,7.5,194.6c0,88.4,175.3,357.4,175.3,357.4S182.9,551.7,182.9,551.7z" />
                    ${text ? `<text x="185" y="280" style="font-size:220px;fill:#ffffff;" text-anchor="middle">${text}</text>` : ''}
                </svg>`;
    };

    /**
     * Handle click event.
     * @param orgUnitNumber - Organization unit number.
     */
    private readonly handleClickEvent = (orgUnitNumber: string | undefined): void => {
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        storeSelectorStateManager?.setSelectedStoreLocationId(undefined);
        if (orgUnitNumber) {
            setTimeout(() => {
                storeSelectorStateManager?.setSelectedStoreLocationId(orgUnitNumber);
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- Time out.
            }, 500);
        }
    };

    /**
     * Get the pushpin option.
     * @param isSelectedLocation - Is selected location.
     * @param index - Index.
     * @param pushpinOptions - PushpinOptions.
     * @returns - The pushpin options.
     */
}

export default Map;
